import React, { useEffect, useState } from 'react';
import { Activity, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Wrench } from 'lucide-react';
import { marketplaceLeads } from '../../services/dealerApi';
import { toast } from 'sonner';

const STATUS_DOT = {
  ONLINE: 'bg-green-400',
  OFFLINE: 'bg-red-400',
  PENDING: 'bg-amber-400',
};

const TEMPLATE_PILL = {
  approved: 'bg-green-500/15 text-green-300 border-green-500/30',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  rejected: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const WhatsAppHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await marketplaceLeads.whatsappHealth();
      setData(res);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const repairWebhooks = async (e) => {
    e?.stopPropagation?.();
    setRepairing(true);
    try {
      const res = await marketplaceLeads.whatsappRepairWebhooks();
      const url = res?.inbound_url || res?.public_base_used;
      toast.success(url ? `Twilio inbound URL set to ${url}` : 'Twilio webhooks repaired');
      await load();
      setExpanded(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!data) return null;
  const ms = data.matching_sender;
  const senderOk = ms?.status === 'ONLINE';
  const tmplsOk = (data.templates || []).every((t) => t.status === 'approved');
  const hasErrors = (data.recent_message_errors || []).length > 0;
  const allGood = senderOk && tmplsOk && !hasErrors;

  return (
    <div
      data-testid="whatsapp-health-card"
      className={`rounded-2xl border backdrop-blur-xl ${
        allGood
          ? 'bg-green-500/5 border-green-500/30'
          : 'bg-red-500/5 border-red-500/30'
      }`}
    >
      {/* Header — outer wrapper is a div (not button) so the inner Refresh
          button below is not nested inside another button. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
        className="w-full flex items-center gap-3 p-4 text-left cursor-pointer select-none"
      >
        {allGood ? (
          <CheckCircle2 className="w-5 h-5 text-green-300 flex-shrink-0" />
        ) : (
          <XCircle className="w-5 h-5 text-red-300 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-white">WhatsApp Health</span>
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${
              allGood
                ? 'bg-green-500/15 text-green-300 border-green-500/30'
                : 'bg-red-500/15 text-red-300 border-red-500/30'
            }`}>
              {allGood ? 'All Systems Go' : 'Delivery Blocked'}
            </span>
          </div>
          {data.hint && (
            <p className="text-xs text-slate-300 mt-1 line-clamp-2">{data.hint}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-slate-400 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="p-1.5 rounded hover:bg-white/5"
            data-testid="wa-health-refresh"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-700/40 p-4 space-y-4 text-sm">
          {/* Sender */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">Sender</div>
            {ms ? (
              <div className="bg-slate-950/40 border border-slate-700/40 rounded-lg p-3 flex items-center gap-3">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT[ms.status] || 'bg-slate-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-slate-200">{ms.sender_id}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Status: <span className="font-semibold text-slate-300">{ms.status}</span>
                    {ms.quality_rating && <> · Quality: <span className="text-slate-300">{ms.quality_rating}</span></>}
                    {ms.messaging_limit && <> · Limit: <span className="text-slate-300">{ms.messaging_limit}</span></>}
                  </div>
                  {(ms.offline_reasons || []).map((r, i) => (
                    <div key={i} className="text-[11px] text-red-300 mt-1">
                      [{r.code}] {r.message}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-200 text-xs">
                No WhatsApp sender matching <code className="font-mono">{data.from_number}</code> is registered on this Twilio account.
              </div>
            )}

            {/* Inbound webhook status — fixes "button replies not captured" */}
            {ms && (() => {
              const cbUrl = ms.callback_url || '';
              const deployHost = typeof window !== 'undefined' ? window.location.host : '';
              const cbHost = (() => { try { return new URL(cbUrl).host; } catch { return ''; } })();
              const hostMatches = cbHost && deployHost && cbHost === deployHost;
              const showMismatchWarning = cbUrl && !hostMatches;
              return (
                <div data-testid="inbound-webhook-row" className={`mt-2 bg-slate-950/40 border rounded-lg p-3 flex items-start gap-3 ${showMismatchWarning ? 'border-amber-500/40' : 'border-slate-700/40'}`}>
                  {cbUrl && !showMismatchWarning ? (
                    <CheckCircle2 className="w-4 h-4 text-green-300 mt-0.5 flex-shrink-0" />
                  ) : showMismatchWarning ? (
                    <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-300 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-semibold ${showMismatchWarning ? 'text-amber-200' : 'text-slate-200'}`}>
                      {cbUrl
                        ? (showMismatchWarning
                            ? 'Webhook points at a DIFFERENT host'
                            : 'Inbound Webhook Configured')
                        : 'Inbound Webhook NOT Configured'}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 break-all font-mono" data-testid="inbound-webhook-url">
                      {cbUrl || 'Twilio is not calling us when customers click buttons or reply.'}
                    </div>
                    {showMismatchWarning && (
                      <div className="text-[11px] text-amber-300 mt-1">
                        Twilio is delivering replies to <span className="font-mono">{cbHost}</span> but this
                        app is running on <span className="font-mono">{deployHost}</span>. Click Repair to point
                        Twilio at this deployment — responses will start reflecting immediately.
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid="repair-webhooks-btn"
                    disabled={repairing}
                    onClick={repairWebhooks}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 text-cyan-200 disabled:opacity-50"
                  >
                    <Wrench className="w-3 h-3" /> {repairing ? 'Repairing…' : 'Repair'}
                  </button>
                </div>
              );
            })()}
            {/* Inbound diagnostics — surfaces WHY replies aren't sticking */}
            <InboundDiagnostics />
          </div>

          {/* Templates */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">
              Templates ({data.templates?.length || 0})
            </div>
            <div className="space-y-1.5">
              {data.templates?.map((t) => (
                <div key={t.sid} className="bg-slate-950/40 border border-slate-700/40 rounded-lg px-3 py-2 flex items-center gap-3">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${TEMPLATE_PILL[t.status] || 'bg-slate-500/15 text-slate-300 border-slate-500/30'}`}>
                    {t.status}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-200 font-semibold truncate">{t.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">{t.sid} · {t.category}</div>
                  </div>
                  {t.rejection_reason && (
                    <div className="text-[11px] text-red-300">{t.rejection_reason}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Recent failures */}
          {data.recent_message_errors?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-red-300 font-bold mb-2">
                Recent Failed Messages ({data.recent_message_errors.length})
              </div>
              <div className="space-y-1.5">
                {data.recent_message_errors.slice(0, 3).map((m, i) => (
                  <div key={i} className="bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                    <div className="text-[11px] text-slate-200">
                      To <span className="font-mono text-slate-300">{m.to}</span>
                      <span className="text-slate-500"> · Error </span>
                      <span className="font-mono text-red-300">{m.error_code}</span>
                    </div>
                    {m.hint && <div className="text-[11px] text-slate-400 mt-0.5">{m.hint}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fix guide */}
          {!allGood && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <div className="flex items-center gap-2 text-amber-300 font-bold text-xs uppercase tracking-wider mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Next Steps
              </div>
              {(() => {
                const recentCode = data.recent_message_errors?.[0]?.error_code;
                // Meta marketing-template throttling: sender is ONLINE but Meta rejects per recipient.
                if (ms?.status === 'ONLINE' && recentCode === 63049) {
                  const marketingTemplates = (data.templates || []).filter((t) => (t.category || '').toUpperCase() === 'MARKETING');
                  return (
                    <div className="text-xs text-slate-200 space-y-2">
                      <p>
                        Your sender is <strong>ONLINE and verified</strong>, but Meta is choosing not to deliver.
                        Error <code className="font-mono text-amber-200">63049</code> means
                        <strong> Meta is throttling your MARKETING templates per recipient</strong> based on their engagement history with marketing messages.
                      </p>
                      <p className="text-slate-300"><strong>Fix (recommended):</strong> change your template category from <code className="font-mono text-amber-200">MARKETING</code> to <code className="font-mono text-amber-200">UTILITY</code>.</p>
                      <ul className="list-disc list-inside space-y-1 text-slate-300">
                        <li>Open Twilio Content Editor → select each template below.</li>
                        <li>Re-submit with category <strong>UTILITY</strong> (acknowledgments, follow-ups, reminders qualify).</li>
                        <li>Wait for Meta re-approval (usually under 30 min).</li>
                        <li>Utility templates are <strong>NOT subject</strong> to per-user marketing throttling.</li>
                      </ul>
                      {marketingTemplates.length > 0 && (
                        <div className="bg-slate-950/40 border border-slate-700/40 rounded p-2 text-[11px]">
                          <div className="text-slate-400 font-bold uppercase tracking-wider mb-1">Templates to re-submit:</div>
                          {marketingTemplates.map((t) => (
                            <div key={t.sid} className="text-slate-200 font-mono">• {t.name}</div>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] text-slate-400 italic">
                        Alternative: retry to the same recipient later (Meta&apos;s per-user limit resets based on engagement).
                      </p>
                      <div className="flex gap-2 flex-wrap pt-1">
                        <a
                          href="https://console.twilio.com/us1/develop/sms/content-template-builder"
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded"
                          data-testid="open-twilio-content-editor"
                        >
                          Open Twilio Content Editor
                        </a>
                        <a
                          href="https://www.twilio.com/docs/api/errors/63049"
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded"
                        >
                          Read about Error 63049
                        </a>
                      </div>
                    </div>
                  );
                }
                // WABA disabled by Meta (verification incomplete)
                if (ms?.status === 'ONLINE' && recentCode === 63112) {
                  return (
                    <div className="text-xs text-slate-200 space-y-2">
                      <p>
                        Twilio shows your sender ONLINE, but <strong>Meta is rejecting at delivery time</strong>.
                        This is a Meta-side issue. Most common causes:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-slate-300">
                        <li><strong>Business verification incomplete</strong> for WABA <code className="font-mono text-amber-200">{ms.waba_id}</code> — check Meta Business Manager → Security Center.</li>
                        <li><strong>Display name not approved</strong> — WhatsApp Manager → &quot;Phone numbers&quot; → check status.</li>
                        <li><strong>Quality rating low or restricted</strong> — quality currently <code className="font-mono text-amber-200">{ms.quality_rating || 'UNKNOWN'}</code>.</li>
                        <li><strong>Messaging limit not set</strong> — currently <code className="font-mono text-amber-200">{ms.messaging_limit || 'Unavailable'}</code>; new senders need Meta to assign a tier.</li>
                      </ul>
                      <div className="flex gap-2 flex-wrap pt-1">
                        <a
                          href={`https://business.facebook.com/wa/manage/phone-numbers/?waba_id=${ms.waba_id}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded"
                        >
                          Open Meta WhatsApp Manager
                        </a>
                        <a
                          href="https://console.twilio.com/us1/develop/sms/senders/whatsapp-senders"
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded"
                        >
                          Open Twilio WhatsApp Senders
                        </a>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="text-xs text-slate-200">
                    {data.recent_message_errors?.[0]?.hint || data.hint || 'Check Twilio Console for sender / template status.'}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppHealthCard;

// ---------------------------------------------------------------------------
// Diagnostics panel — displays the last 20 attempted Twilio inbound POSTs
// and their outcomes, so the dealer can self-diagnose why customer replies
// aren't landing in the Responded bucket.
// ---------------------------------------------------------------------------
function InboundDiagnostics() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await marketplaceLeads.whatsappInboundDebug();
      setRows(res.entries || []);
      setSummary(res.summary || {});
    } catch {
      /* soft-fail */
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && rows.length === 0) await load();
  };

  const totalIssues = (summary.signature_failed || 0) + (summary.lead_not_found || 0);
  const showAlert = totalIssues > 0;

  return (
    <div className="mt-2 bg-slate-950/40 border border-slate-700/40 rounded-lg" data-testid="inbound-diagnostics">
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        className="w-full flex items-center gap-3 p-3 text-left cursor-pointer select-none"
      >
        <Activity className={`w-4 h-4 ${showAlert ? 'text-amber-300' : 'text-slate-400'}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-semibold ${showAlert ? 'text-amber-200' : 'text-slate-200'}`}>
            Inbound Diagnostics {showAlert && <span className="text-amber-300">· {totalIssues} recent issue{totalIssues === 1 ? '' : 's'}</span>}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Recent Twilio POSTs to /whatsapp/incoming. Click to inspect.
          </div>
        </div>
        {loading ? <RefreshCw className="w-3.5 h-3.5 text-slate-400 animate-spin" /> : (open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />)}
      </div>

      {open && (
        <div className="p-3 border-t border-slate-800 space-y-2" data-testid="inbound-diagnostics-body">
          {/* Summary counts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[11px]">
            {Object.entries(summary).map(([k, v]) => (
              <div key={k} className={`px-2 py-1.5 rounded border ${v > 0 && (k === 'signature_failed' || k === 'lead_not_found') ? 'border-amber-500/40 bg-amber-500/5 text-amber-200' : 'border-slate-700/40 bg-slate-950/30 text-slate-300'}`}>
                <span className="font-mono">{v}</span> · {k.replace(/_/g, ' ')}
              </div>
            ))}
          </div>

          {/* Entries */}
          {rows.length === 0 ? (
            <div className="text-[11px] text-slate-500 p-3 text-center">
              No inbound webhook POSTs recorded yet. If customers are clicking buttons and nothing appears here, Twilio is not reaching this deployment. Click <strong className="text-cyan-300">Repair</strong> above.
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {rows.map((r, i) => (
                <div key={i} className="text-[11px] bg-slate-950/50 border border-slate-800 rounded p-2 font-mono">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-slate-500">{r.received_at ? new Date(r.received_at + (String(r.received_at).endsWith('Z') ? '' : 'Z')).toLocaleTimeString('en-IN') : ''}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.outcome === 'signature_failed' ? 'bg-red-500/15 text-red-300' : (r.outcome || '').startsWith('updated') ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>{r.outcome || 'unknown'}</span>
                    <span className="text-slate-400">from {r.from_raw || '—'}</span>
                    {r.button_text && <span className="text-cyan-300">btn: {r.button_text}</span>}
                  </div>
                  {r.outcome === 'signature_failed' && r.forwarded && (
                    <div className="text-slate-500 mt-1 text-[10px] break-all">
                      request_url: {r.request_url}<br />
                      x-forwarded-host: {r.forwarded['x-forwarded-host'] || 'MISSING'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <button onClick={load} className="w-full text-[11px] px-2 py-1.5 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300" data-testid="inbound-diagnostics-refresh">
            Refresh diagnostics
          </button>
          <button
            onClick={async () => {
              try {
                const res = await marketplaceLeads.whatsappReplayInbound();
                toast.success(`Backfill complete — ${res.leads_marked_responded} leads marked Responded from ${res.scanned_entries} logged replies`);
                await load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Backfill failed');
              }
            }}
            data-testid="inbound-replay-btn"
            className="w-full text-[11px] px-2 py-1.5 rounded bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-200"
          >
            Backfill missed replies to my leads
          </button>
        </div>
      )}
    </div>
  );
}
